// Firebase imports
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { isResourcePurchaseCompleted } from '@/lib/resource-purchases-status';
import { DRXError, toDRXError } from '@/lib/drx-error';
import { IPurchaseRepository } from './interfaces';

// Purchase entity interfaces
export interface CreatePurchaseData {
  userId: string;
  cartId?: string;
  payment_method: 'stripe' | 'paypal' | 'apple_pay' | 'google_pay';
  payment_status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
  totalAmount: number;
  subtotalAmount: number;
  discountAmount?: number;
  taxAmount?: number;
  shippingAmount?: number;
  currency: string;
  paymentIntentId?: string;
  paymentSessionId?: string;
  billingAddress?: Record<string, any>;
  shippingAddress?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface UpdatePurchaseData {
  payment_status?: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
  paymentIntentId?: string;
  paymentSessionId?: string;
  billingAddress?: Record<string, any>;
  shippingAddress?: Record<string, any>;
  metadata?: Record<string, any>;
  updatedAt?: Timestamp;
}

export interface PurchaseFilter {
  userId?: string;
  payment_status?: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
  payment_method?: 'stripe' | 'paypal' | 'apple_pay' | 'google_pay';
  createdAfter?: Date;
  createdBefore?: Date;
  totalMin?: number;
  totalMax?: number;
  currency?: string;
}

export interface GetPurchaseData {
  id: string;
  userId: string;
  cartId?: string;
  payment_method: 'stripe' | 'paypal' | 'apple_pay' | 'google_pay';
  payment_status: 'pending' | 'processing' | 'completed' | 'failed' | 'refunded';
  totalAmount: number;
  subtotalAmount: number;
  discountAmount: number;
  taxAmount: number;
  shippingAmount: number;
  currency: string;
  paymentIntentId?: string;
  paymentSessionId?: string;
  billingAddress?: Record<string, any>;
  shippingAddress?: Record<string, any>;
  metadata?: Record<string, any>;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface CreatePurchaseItemData {
  purchaseId: string;
  productType: 'course' | 'resource' | 'subscription' | 'connect';
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  discountAmount?: number;
  metadata?: Record<string, any>;
}

export interface GetPurchaseItemData {
  id: string;
  purchaseId: string;
  productType: 'course' | 'resource' | 'subscription' | 'connect';
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  discountAmount: number;
  metadata?: Record<string, any>;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export class PurchaseRepository implements IPurchaseRepository {
  constructor() {
    console.log('[PurchaseRepository] Constructor - Using client-side Firebase DB instance:', !!db);
  }

  private collectionName = 'resourcePurchases';
  private itemsCollectionName = 'purchaseItems';
  private resourcePurchasesCollection = 'resourcePurchases';

  private toRepositoryError(error: unknown, code: string, devMessage: string) {
    return toDRXError(error, {
      code,
      devMessage,
    });
  }

  // Create purchase
  async createPurchase(purchaseData: CreatePurchaseData): Promise<GetPurchaseData> {
    console.log('[PurchaseRepository.createPurchase] START - PurchaseData:', purchaseData);
    try {
      console.log('[PurchaseRepository.createPurchase] Creating purchase document in Firestore');
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...purchaseData,
        discountAmount: purchaseData.discountAmount || 0,
        taxAmount: purchaseData.taxAmount || 0,
        shippingAmount: purchaseData.shippingAmount || 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const newPurchase = await this.getPurchaseById(docRef.id);
      console.log('[PurchaseRepository.createPurchase] SUCCESS - Created purchase with ID:', docRef.id);
      return newPurchase!;
    } catch (error) {
      console.error('[PurchaseRepository.createPurchase] ERROR:', error);
      throw this.toRepositoryError(error, 'PURCHASE_CREATE_FAILED', 'Failed to create purchase');
    }
  }

  // Get purchase by ID
  async getPurchaseById(purchaseId: string): Promise<GetPurchaseData | null> {
    console.log('[PurchaseRepository.getPurchaseById] START - PurchaseId:', purchaseId);
    try {
      console.log('[PurchaseRepository.getPurchaseById] Fetching purchase document from Firestore');
      const docSnapshot = await getDocs(query(collection(db, this.collectionName), where('__name__', '==', purchaseId)));
      
      if (docSnapshot.empty) {
        console.log('[PurchaseRepository.getPurchaseById] Purchase not found');
        return null;
      }

      const purchaseDoc = docSnapshot.docs[0];
      const purchase = {
        id: purchaseDoc.id,
        ...purchaseDoc.data(),
      } as GetPurchaseData;

      console.log('[PurchaseRepository.getPurchaseById] SUCCESS - Found purchase');
      return purchase;
    } catch (error) {
      console.error('[PurchaseRepository.getPurchaseById] ERROR:', error);
      throw this.toRepositoryError(error, 'PURCHASE_FETCH_FAILED', `Failed to get purchase ${purchaseId}`);
    }
  }

  // Get purchases with filters
  async getPurchases(filter: PurchaseFilter = {}, limitCount?: number): Promise<GetPurchaseData[]> {
    console.log('[PurchaseRepository.getPurchases] START - Filter:', filter, 'LimitCount:', limitCount);
    console.log('[PurchaseRepository.getPurchases] Collection name:', this.collectionName);
    try {
      console.log('[PurchaseRepository.getPurchases] Building Firestore query with filters');
      let q = collection(db, this.collectionName);
      
      // First, let's try a simple query to see if collection exists
      console.log('[PurchaseRepository.getPurchases] Testing basic collection access...');
      const testSnapshot = await getDocs(q);
      console.log('[PurchaseRepository.getPurchases] Collection test - Document count:', testSnapshot.size);

      // Build query constraints array
      const constraints = [];

      // Add filters
      if (filter.userId) {
        constraints.push(where('userId', '==', filter.userId));
      }
      if (filter.payment_status) {
        constraints.push(where('payment_status', '==', filter.payment_status));
      }
      if (filter.payment_method) {
        constraints.push(where('payment_method', '==', filter.payment_method));
      }
      if (filter.currency) {
        constraints.push(where('currency', '==', filter.currency));
      }
      if (filter.createdAfter) {
        constraints.push(where('createdAt', '>=', filter.createdAfter));
      }
      if (filter.createdBefore) {
        constraints.push(where('createdAt', '<=', filter.createdBefore));
      }
      if (filter.totalMin) {
        constraints.push(where('totalAmount', '>=', filter.totalMin));
      }
      if (filter.totalMax) {
        constraints.push(where('totalAmount', '<=', filter.totalMax));
      }

      // Add ordering and limit
      constraints.push(orderBy('createdAt', 'desc'));
      if (limitCount) {
        constraints.push(limit(limitCount));
      }

      // Build the final query
      if (constraints.length > 0) {
        q = query(q, ...constraints);
        console.log('[PurchaseRepository.getPurchases] Built query with', constraints.length, 'constraints');
      }

      const querySnapshot = await getDocs(q);
      const purchases: GetPurchaseData[] = [];

      querySnapshot.forEach((doc) => {
        purchases.push({
          id: doc.id,
          ...doc.data(),
        } as GetPurchaseData);
      });

      console.log('[PurchaseRepository.getPurchases] SUCCESS - Retrieved', purchases.length, 'purchases');
      return purchases;
    } catch (error) {
      console.error('[PurchaseRepository.getPurchases] ERROR:', error);
      
      // If it's a permission error or collection doesn't exist, return empty array
      if (error instanceof Error && (
        error.message.includes('permission') ||
        error.message.includes('not found') ||
        error.message.includes('Missing or insufficient permissions')
      )) {
        console.log('[PurchaseRepository.getPurchases] Collection may not exist or no permissions, returning empty array');
        return [];
      }
      
      throw this.toRepositoryError(error, 'PURCHASES_FETCH_FAILED', 'Failed to get purchases');
    }
  }

  // Get all purchases (admin access)
  async getAllPurchases(limitCount?: number): Promise<GetPurchaseData[]> {
    console.log('[PurchaseRepository.getAllPurchases] START - LimitCount:', limitCount);
    try {
      console.log('[PurchaseRepository.getAllPurchases] Retrieving all purchases (admin access)');
      const result = await this.getPurchases({}, limitCount);
      console.log('[PurchaseRepository.getAllPurchases] SUCCESS - Retrieved', result.length, 'purchases');
      return result;
    } catch (error) {
      console.error('[PurchaseRepository.getAllPurchases] ERROR:', error);
      throw this.toRepositoryError(error, 'PURCHASES_ALL_FETCH_FAILED', 'Failed to get all purchases');
    }
  }

  // Update purchase
  async updatePurchase(purchaseId: string, updateData: UpdatePurchaseData): Promise<GetPurchaseData> {
    console.log('[PurchaseRepository.updatePurchase] START - PurchaseId:', purchaseId, 'UpdateData:', updateData);
    try {
      console.log('[PurchaseRepository.updatePurchase] Updating purchase document in Firestore');
      const docRef = doc(db, this.collectionName, purchaseId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      const updatedPurchase = await this.getPurchaseById(purchaseId);
      console.log('[PurchaseRepository.updatePurchase] SUCCESS - Updated purchase');
      return updatedPurchase!;
    } catch (error) {
      console.error('[PurchaseRepository.updatePurchase] ERROR:', error);
      throw this.toRepositoryError(error, 'PURCHASE_UPDATE_FAILED', `Failed to update purchase ${purchaseId}`);
    }
  }

  // Delete purchase
  async deletePurchase(purchaseId: string): Promise<void> {
    console.log('[PurchaseRepository.deletePurchase] START - PurchaseId:', purchaseId);
    try {
      console.log('[PurchaseRepository.deletePurchase] Deleting purchase and its items');
      
      // Delete all purchase items first
      await this.deleteAllPurchaseItems(purchaseId);

      // Delete the purchase
      const docRef = doc(db, this.collectionName, purchaseId);
      await deleteDoc(docRef);
      console.log('[PurchaseRepository.deletePurchase] SUCCESS - Deleted purchase and related data');
    } catch (error) {
      console.error('[PurchaseRepository.deletePurchase] ERROR:', error);
      throw this.toRepositoryError(error, 'PURCHASE_DELETE_FAILED', `Failed to delete purchase ${purchaseId}`);
    }
  }

  // Get user purchases from resourcePurchases collection
  async getUserPurchases(userId: string, limitCount?: number): Promise<any[]> {
    console.log('[PurchaseRepository.getUserPurchases] START - UserId:', userId, 'LimitCount:', limitCount);
    try {
      console.log('[PurchaseRepository.getUserPurchases] Finding resource purchases for user from resourcePurchases collection');
      console.log('🔥 [PurchaseRepository.getUserPurchases] Firebase Auth UserId:', userId);

      // Build query for resourcePurchases collection (removed orderBy to avoid index requirement)
      const constraints = [
        where('userId', '==', userId)
      ];

      if (limitCount) {
        constraints.push(limit(limitCount));
      }

      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);

      console.log('[PurchaseRepository.getUserPurchases] Found', querySnapshot.size, 'resource purchase documents');

      const purchases: any[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const hasResourceId = typeof data.resourceId === 'string' && data.resourceId.trim().length > 0;
        if (!hasResourceId) {
          console.warn(
            '[PurchaseRepository.getUserPurchases] Skipping non-resource or malformed purchase row:',
            doc.id,
          );
          return;
        }

        const status = String(data.status || '').trim();
        if (!isResourcePurchaseCompleted(status)) {
          console.warn(
            '[PurchaseRepository.getUserPurchases] Skipping non-completed purchase row:',
            doc.id,
            status,
          );
          return;
        }

        const sellerOwnerId = typeof data.sellerId === 'string' ? data.sellerId : '';
        const instructorOwnerId = typeof data.instructorId === 'string' ? data.instructorId : '';
        const isSelfOwnedPurchase =
          (sellerOwnerId.length > 0 && sellerOwnerId === userId) ||
          (instructorOwnerId.length > 0 && instructorOwnerId === userId);

        // Keep "My Purchases" buyer-only. Creator-owned items belong in seller/pro dashboards, not buyer history.
        if (isSelfOwnedPurchase) {
          console.warn(
            '[PurchaseRepository.getUserPurchases] Skipping creator-owned purchase row from buyer history:',
            doc.id,
          );
          return;
        }

        // Map resourcePurchases structure to GetPurchaseData structure
        const mappedPurchase: any = {
          id: doc.id,
          userId: data.userId || userId,
          payment_method: 'stripe' as const, // Default to stripe
          payment_status: (isResourcePurchaseCompleted(data.status)
            ? "completed"
            : "pending") as const,
          totalAmount: data.amount || 0,
          subtotalAmount: data.amount || 0,
          discountAmount: 0,
          taxAmount: 0,
          shippingAmount: 0,
          currency: 'usd',
          paymentIntentId: undefined,
          paymentSessionId: undefined,
          billingAddress: undefined,
          shippingAddress: undefined,
          metadata: {
            resourceId: data.resourceId,
            itemType: data.itemType,
            title: data.title,
            originalStructure: 'resourcePurchases'
          },
          createdAt: data.createdAt?.toDate?.() || data.purchasedAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || data.createdAt?.toDate?.() || data.purchasedAt?.toDate?.() || new Date(),
          // Same line-item shape as `orders.items`: single id field `itemId`
          items: [{
            itemId: data.resourceId,
            title: data.title,
            price: data.amount || 0,
            amount: data.amount || 0,
            itemType: data.itemType || 'resource',
            quantity: 1
          }]
        };

        purchases.push(mappedPurchase);
      });

      console.log('[PurchaseRepository.getUserPurchases] SUCCESS - Mapped', purchases.length, 'purchases from resourcePurchases collection');
      return purchases;
    } catch (error) {
      console.error('[PurchaseRepository.getUserPurchases] ERROR:', error);

      // For user purchases, it's common to have no purchases yet, so return empty array
      console.log('[PurchaseRepository.getUserPurchases] Returning empty array for user with no purchases');
      return [];
    }
  }

  // Get purchases by payment status
  async getPurchasesByStatus(
    status: PurchaseFilter['payment_status'],
    limitCount?: number,
  ): Promise<GetPurchaseData[]> {
    console.log('[PurchaseRepository.getPurchasesByStatus] START - Status:', status, 'LimitCount:', limitCount);
    try {
      const filter: PurchaseFilter = { payment_status: status };
      const purchases = await this.getPurchases(filter, limitCount);
      
      console.log('[PurchaseRepository.getPurchasesByStatus] SUCCESS - Found', purchases.length, 'purchases');
      return purchases;
    } catch (error) {
      console.error('[PurchaseRepository.getPurchasesByStatus] ERROR:', error);
      throw this.toRepositoryError(error, 'PURCHASES_BY_STATUS_FETCH_FAILED', `Failed to get purchases with status ${status}`);
    }
  }

  // ===== PURCHASE ITEMS METHODS =====

  // Add item to purchase
  async addPurchaseItem(itemData: CreatePurchaseItemData): Promise<GetPurchaseItemData> {
    console.log('[PurchaseRepository.addPurchaseItem] START - ItemData:', itemData);
    try {
      console.log('[PurchaseRepository.addPurchaseItem] Adding item to purchase');
      const docRef = await addDoc(collection(db, this.itemsCollectionName), {
        ...itemData,
        discountAmount: itemData.discountAmount || 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const newItem = await this.getPurchaseItemById(docRef.id);
      console.log('[PurchaseRepository.addPurchaseItem] SUCCESS - Added purchase item with ID:', docRef.id);
      return newItem!;
    } catch (error) {
      console.error('[PurchaseRepository.addPurchaseItem] ERROR:', error);
      throw this.toRepositoryError(error, 'PURCHASE_ITEM_CREATE_FAILED', `Failed to add item to purchase ${itemData.purchaseId}`);
    }
  }

  // Get purchase item by ID
  async getPurchaseItemById(itemId: string): Promise<GetPurchaseItemData | null> {
    console.log('[PurchaseRepository.getPurchaseItemById] START - ItemId:', itemId);
    try {
      const docSnapshot = await getDocs(query(collection(db, this.itemsCollectionName), where('__name__', '==', itemId)));
      
      if (docSnapshot.empty) {
        console.log('[PurchaseRepository.getPurchaseItemById] Purchase item not found');
        return null;
      }

      const itemDoc = docSnapshot.docs[0];
      const item = {
        id: itemDoc.id,
        ...itemDoc.data(),
      } as GetPurchaseItemData;

      console.log('[PurchaseRepository.getPurchaseItemById] SUCCESS - Found purchase item');
      return item;
    } catch (error) {
      console.error('[PurchaseRepository.getPurchaseItemById] ERROR:', error);
      throw this.toRepositoryError(error, 'PURCHASE_ITEM_FETCH_FAILED', `Failed to get purchase item ${itemId}`);
    }
  }

  // Get all items in a purchase
  async getPurchaseItems(purchaseId: string): Promise<GetPurchaseItemData[]> {
    console.log('[PurchaseRepository.getPurchaseItems] START - PurchaseId:', purchaseId);
    try {
      console.log('[PurchaseRepository.getPurchaseItems] Fetching all items for purchase');
      const q = query(
        collection(db, this.itemsCollectionName),
        where('purchaseId', '==', purchaseId),
        orderBy('createdAt', 'asc')
      );

      const querySnapshot = await getDocs(q);
      const items: GetPurchaseItemData[] = [];

      querySnapshot.forEach((doc) => {
        items.push({
          id: doc.id,
          ...doc.data(),
        } as GetPurchaseItemData);
      });

      console.log('[PurchaseRepository.getPurchaseItems] SUCCESS - Retrieved', items.length, 'purchase items');
      return items;
    } catch (error) {
      console.error('[PurchaseRepository.getPurchaseItems] ERROR:', error);
      throw this.toRepositoryError(error, 'PURCHASE_ITEMS_FETCH_FAILED', `Failed to get items for purchase ${purchaseId}`);
    }
  }

  // Delete all purchase items
  async deleteAllPurchaseItems(purchaseId: string): Promise<void> {
    console.log('[PurchaseRepository.deleteAllPurchaseItems] START - PurchaseId:', purchaseId);
    try {
      console.log('[PurchaseRepository.deleteAllPurchaseItems] Getting all items for purchase before deletion');
      const items = await this.getPurchaseItems(purchaseId);
      console.log('[PurchaseRepository.deleteAllPurchaseItems] Deleting', items.length, 'items');
      
      const deletePromises = items.map(item =>
        deleteDoc(doc(db, this.itemsCollectionName, item.id))
      );
      await Promise.all(deletePromises);
      console.log('[PurchaseRepository.deleteAllPurchaseItems] SUCCESS - Deleted all purchase items');
    } catch (error) {
      console.error('[PurchaseRepository.deleteAllPurchaseItems] ERROR:', error);
      throw this.toRepositoryError(error, 'PURCHASE_ITEMS_DELETE_FAILED', `Failed to delete items for purchase ${purchaseId}`);
    }
  }

  // Get revenue analytics
  async getRevenueAnalytics(startDate?: Date, endDate?: Date): Promise<{
    totalRevenue: number;
    totalTransactions: number;
    averageOrderValue: number;
    topProducts: Array<{ productId: string; revenue: number; count: number }>;
  }> {
    console.log('[PurchaseRepository.getRevenueAnalytics] START - DateRange:', startDate, 'to', endDate);
    try {
      const filter: PurchaseFilter = { 
        payment_status: 'completed',
        ...(startDate && { createdAfter: startDate }),
        ...(endDate && { createdBefore: endDate })
      };
      
      const purchases = await this.getPurchases(filter);
      
      const totalRevenue = purchases.reduce((sum, p) => sum + p.totalAmount, 0);
      const totalTransactions = purchases.length;
      const averageOrderValue = totalTransactions > 0 ? totalRevenue / totalTransactions : 0;

      // Calculate top products (simplified version)
      const productMap = new Map<string, { revenue: number; count: number }>();
      
      for (const purchase of purchases) {
        const items = await this.getPurchaseItems(purchase.id);
        for (const item of items) {
          const existing = productMap.get(item.productId) || { revenue: 0, count: 0 };
          productMap.set(item.productId, {
            revenue: existing.revenue + item.totalPrice,
            count: existing.count + item.quantity
          });
        }
      }

      const topProducts = Array.from(productMap.entries())
        .map(([productId, data]) => ({ productId, ...data }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);

      console.log('[PurchaseRepository.getRevenueAnalytics] SUCCESS - Analytics calculated');
      return {
        totalRevenue,
        totalTransactions,
        averageOrderValue,
        topProducts
      };
    } catch (error) {
      console.error('[PurchaseRepository.getRevenueAnalytics] ERROR:', error);
      throw this.toRepositoryError(error, 'PURCHASE_REVENUE_ANALYTICS_FAILED', 'Failed to get revenue analytics');
    }
  }

  // ===== RESOURCE PURCHASES (absorbed from firestore.ts) =====

  async getUserResourcePurchases(userId: string): Promise<any[]> {
    try {
      const q = query(
        collection(db, this.resourcePurchasesCollection),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      throw new DRXError({
        code: 'RESOURCE_PURCHASE_FETCH_FAILED',
        devMessage: `Failed to fetch resource purchases for user ${userId}`,
        cause: error,
      });
    }
  }

  async getUserResourceOrders(userId: string): Promise<any[]> {
    try {
      const q = query(
        collection(db, 'orders'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
      throw new DRXError({
        code: 'RESOURCE_ORDER_FETCH_FAILED',
        devMessage: `Failed to fetch resource orders for user ${userId}`,
        cause: error,
      });
    }
  }

  async getResourcePurchasesForOwner(
    ownerId: string,
  ): Promise<Array<Record<string, unknown> & { id: string }>> {
    try {
      const col = collection(db, this.resourcePurchasesCollection);
      const q1 = query(col, where('instructorId', '==', ownerId), orderBy('purchasedAt', 'desc'));
      const q2 = query(col, where('sellerId', '==', ownerId), orderBy('purchasedAt', 'desc'));
      const [snapshot1, snapshot2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      const purchasesMap = new Map<string, Record<string, unknown> & { id: string }>();
      snapshot1.forEach((d) => {
        purchasesMap.set(d.id, { id: d.id, ...d.data() } as Record<string, unknown> & { id: string });
      });
      snapshot2.forEach((d) => {
        if (!purchasesMap.has(d.id)) {
          purchasesMap.set(d.id, { id: d.id, ...d.data() } as Record<string, unknown> & { id: string });
        }
      });
      return Array.from(purchasesMap.values());
    } catch (error) {
      throw this.toRepositoryError(
        error,
        'RESOURCE_PURCHASES_OWNER_FETCH_FAILED',
        `Failed to fetch resource purchases for owner ${ownerId}`,
      );
    }
  }
}
